import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useToast } from '../components/ToastProvider.jsx'
import * as authService from '../services/auth'
import { canManageProjects, canManageUsers, canTransact, isStaff, roleLabel } from '../utils/permissions'

const EMPTY_PASSWORD_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

export default function SettingsPage() {
  const { user, logout } = useAuth()
  const { show } = useToast()
  const navigate = useNavigate()
  const manageUsers = canManageUsers(user)
  const manageProjects = canManageProjects(user)
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordSaving, setPasswordSaving] = useState(false)

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  function setPasswordField(name, value) {
    setPasswordForm((current) => ({ ...current, [name]: value }))
    setPasswordError('')
    setPasswordSuccess('')
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordError('현재 비밀번호와 새 비밀번호를 입력해 주세요.')
      return
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('새 비밀번호는 6자 이상이어야 합니다.')
      return
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('새 비밀번호 확인이 일치하지 않습니다.')
      return
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('새 비밀번호는 현재 비밀번호와 달라야 합니다.')
      return
    }

    setPasswordSaving(true)
    try {
      await authService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      })
      setPasswordForm(EMPTY_PASSWORD_FORM)
      setPasswordSuccess('비밀번호가 변경되었습니다.')
      show('비밀번호가 변경되었습니다.')
    } catch (err) {
      setPasswordError(err.message || '비밀번호를 변경하지 못했습니다.')
    } finally {
      setPasswordSaving(false)
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="설정"
        description="내 계정 정보를 확인하고 비밀번호를 변경합니다."
        actions={
          manageProjects || manageUsers ? (
            <>
              {manageProjects ? (
                <Link to="/settings/projects" className="btn btn--ghost btn--touch">
                  프로젝트 관리
                </Link>
              ) : null}
              {manageUsers ? (
                <Link to="/settings/users" className="btn btn--primary btn--touch">
                  사용자 관리
                </Link>
              ) : null}
            </>
          ) : null
        }
      />

      <section className="panel settings-block">
        <h2>로그인 사용자</h2>
        <dl className="spec-grid">
          <div>
            <dt>이름</dt>
            <dd>{user?.name}</dd>
          </div>
          <div>
            <dt>아이디</dt>
            <dd>{user?.username}</dd>
          </div>
          <div>
            <dt>부서</dt>
            <dd>{user?.department || '-'}</dd>
          </div>
          <div>
            <dt>직책</dt>
            <dd>{user?.position || '-'}</dd>
          </div>
          <div>
            <dt>권한</dt>
            <dd>{roleLabel(user?.role)}</dd>
          </div>
          <div>
            <dt>입출고</dt>
            <dd>{isStaff(user) ? (canTransact(user) ? '허용' : '없음') : '항상 가능'}</dd>
          </div>
        </dl>
        <button type="button" className="btn btn--ghost btn--touch" onClick={handleLogout}>
          로그아웃
        </button>
      </section>

      <section className="panel settings-block">
        <h2>비밀번호 변경</h2>
        <p className="muted">본인 계정 비밀번호만 변경할 수 있습니다.</p>
        <form className="password-form" onSubmit={handlePasswordSubmit}>
          <label className="field">
            <span>현재 비밀번호</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordField('currentPassword', e.target.value)}
            />
          </label>
          <label className="field">
            <span>새 비밀번호</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordField('newPassword', e.target.value)}
            />
          </label>
          <label className="field">
            <span>새 비밀번호 확인</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordField('confirmPassword', e.target.value)}
            />
          </label>
          {passwordError ? <p className="form-error">{passwordError}</p> : null}
          {passwordSuccess ? <p className="form-success">{passwordSuccess}</p> : null}
          <button type="submit" className="btn btn--primary btn--touch" disabled={passwordSaving}>
            {passwordSaving ? '변경 중...' : '비밀번호 변경'}
          </button>
        </form>
      </section>
    </div>
  )
}
